<template>
    <div class="card-body">
        <h5 v-if="showTitle" class="card-title">{{ $t('registers.events.evaluation.tempRanking.title') }}</h5>

        <div class="form-row">
            <div class="form-group col-md-6 col-sm-12">
                <label class="col-form-label font-weight-bold" :for="'tempRankingName' + _uid">
                    {{$t('editor.common.name')}}
                </label>
                <input v-if="editing" v-model="tempRanking.name" type="text" class="form-control"
                        :id="'tempRankingName' + _uid" :aria-describedby="'tempRankingNameHelp' + _uid"
                        :placeholder="$t('editor.common.name')">
                <input v-else type="text" readonly class="form-control-plaintext" :id="'tempRankingName' + _uid"
                        :value="tempRanking.name">
                <small :id="'tempRankingNameHelp' + _uid" class="form-text text-muted">
                    {{$t('registers.events.evaluation.tempRanking.eventName')}}
                </small>
            </div>

            <div class="form-group col-md-4 col-sm-12">
                <label class="col-form-label font-weight-bold" :for="'confirmMaxDate' + _uid">
                    {{$t('registers.events.evaluation.tempRanking.confirmMaxDate')}}
                </label>
                <div v-if="editing">
                    <DateTimePicker :id="'confirmMaxDate' + _uid"
                                    v-model="tempRanking.confirmMaxDate"
                                    :min="tempRanking.confirmDateMinRange"
                                    :max="tempRanking.confirmDateMaxRange"
                                    :readonly="!editing"
                                    :showTime="true"
                                    :bindPopup="true">
                    </DateTimePicker>
                </div>
                <div v-else>
                    <DateTimePicker :id="'confirmMaxDate' + _uid" v-model="tempRanking.confirmMaxDate"
                        :readonly="true">
                    </DateTimePicker>

                </div>
            </div>

            <div class="form-group col-md-1 col-sm-6">
                <label class="col-form-label font-weight-bold" :for="'isActive' + _uid">
                    {{$t('registers.events.evaluation.tempRanking.active')}}
                </label>
                <div>
                    <span v-if="tempRanking.isActive" :id="'isActive' + _uid" class="badge badge-success"> {{ $t('yes') }}</span>
                    <span v-else :id="'isActive' + _uid" class="badge badge-danger">{{ $t('no') }}</span>
                </div>
            </div>

            <div class="form-group col-md-1 col-sm-6">
                <label class="col-form-label font-weight-bold" :for="'tempRankingVersion' + _uid">
                    {{ $t('registers.events.evaluation.tempRanking.version') }}
                </label>
                <div>
                    <span :id="'tempRankingVersion' + _uid" class="badge badge-light">{{ tempRanking.ver }}</span>
                </div>
            </div>

            <div class="form-group col-md-6 col-sm-12">
                <div>
                    <vs-checkbox v-model="tempRanking.accommodationRequestRequired"
                                    :name="'accommodationRequestRequired' + _uid"
                                    :id="'accommodationRequestRequired' + _uid"
                                    :disabled="!editing"
                                    style="justify-content: flex-start !important"
                                    aria-describedby="accommodationRequestRequiredHelp">
                        {{ $t('registers.events.evaluation.tempRanking.accommodationRequestRequired') }}
                    </vs-checkbox>
                    <small id="accommodationRequestRequiredHelp" class="form-text text-muted">
                        {{$t('registers.events.evaluation.tempRanking.accommodationRequestRequiredHelp')}}
                    </small>
                </div>
            </div>

            <div class="form-group col-md-6 col-sm-12 text-right">
                <button class="btn btn-link text-info"
                            @click.stop="showRankingDetailsModal">
                        <i class="fas fa-eye"></i>
                        {{ $t('buttons.rankingDetails.btnText')}}
                    </button>
            </div>
        </div>

        <hr />

        <slot name="event-details">
            <ExpandableDetails class="m-0" v-if="tempRanking"
                                :title="$t('registers.events.evaluation.tempRanking.eventDetails') + (tempRanking.eventName ? (' - ' + tempRanking.eventName) : '')"
                                :showInCard="true">
                <template v-slot:content>
                    <div class="form-row">
                        <div class="form-group col-xl-3 col-md-6 col-sm-12">
                            <label class="col-form-label font-weight-bold" :for="'eventName' + _uid">
                                {{ $t('registers.forms.gridHeaders.eventName') }}
                            </label>
                            <input type="text" readonly class="form-control-plaintext" :id="'eventName' + _uid"
                                    :value="tempRanking.eventName">
                        </div>
                        <div class="form-group col-xl-3 col-md-6 col-sm-12">
                            <label class="col-form-label font-weight-bold" :for="'participantsMaxNum' + _uid">
                                {{ $t('registers.events.generalData.participantsMaxNum') }}
                            </label>
                            <input type="number" readonly class="form-control-plaintext" :id="'participantsMaxNum' + _uid"
                                    :value="tempRanking.studentsMaxNum">
                        </div>
                        <div class="form-group col-xl-3 col-md-6 col-sm-12">
                            <label class="col-form-label font-weight-bold" :for="'eventStartDate'">
                                {{$t('registers.events.evaluation.tempRanking.eventStartDate')}}
                            </label>
                            <DateTimePicker :id="'eventStartDate' + _uid" v-model="tempRanking.eventStartDate"
                                            :readonly="true">
                            </DateTimePicker>
                        </div>
                        <div class="form-group col-xl-3 col-md-6 col-sm-12">
                            <label class="col-form-label font-weight-bold" :for="'eventEndDate' + _uid">
                                {{ $t('registers.events.evaluation.tempRanking.eventEndDate')}}
                            </label>
                            <DateTimePicker :id="'eventEndDate' + _uid" v-model="tempRanking.eventEndDate"
                                            :readonly="true">
                            </DateTimePicker>
                        </div>
                    </div>
                </template>
            </ExpandableDetails>
        </slot>

        <slot name="statistics"></slot>

        <slot name="participants"></slot>

        <slot name="actions">
            <div class="mt-4"></div>
        </slot>

        <slot name="footer">
            <div class="card-footer text-muted">
                <span>{{ $t('lastUpdatedBy') }} <strong>{{ tempRanking.updaterUsername ? tempRanking.updaterUsername : tempRanking.creatorUsername }}</strong></span>
                <span v-if="tempRanking.createdAt || tempRanking.updatedAt">{{ ' - ' + this.$moment(tempRanking.updatedAt ? tempRanking.updatedAt : tempRanking.createdAt).format('DD.MM.YYYY HH:mm:ss') }}</span>
            </div>
        </slot>

        <JsonFormatter v-model="rankingDetails" :show.sync="showRankingDetails"
                       :titleKey="'buttons.details.btnText'"
                       @onClose="onRankingDetailsClose">
        </JsonFormatter>
    </div>
</template>

<script>
import { TempRanking } from '@/models/models';
import DateTimePicker from '@/components/datepicker/DateTimePicker';
import ExpandableDetails from '@/components/common/ExpandableDetails';
import JsonFormatter from "@/components/common/json.formatter.modal.vue";

export default {
    props: {
        tempRanking: {
            required: true,
            type: TempRanking,
        },
        editing: {
            required: true,
            type: Boolean,
            default: false
        },
        showTitle: {
            type: Boolean,
            default: true
        }
    },
    components: {
        DateTimePicker,
        ExpandableDetails,
        JsonFormatter
    },
    data() {
        return {
            rankingDetails: '',
            showRankingDetails: false
        }
    },
    methods: {
        showRankingDetailsModal() {
            this.$http.get(`/api/tempRanking/getTempRankingDetails/${this.tempRanking.id}`)
                .then(response => {
                    this.rankingDetails = response.data;
                    this.showRankingDetails = true;
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('details'),
                        text: this.$helpers.errorText(error),
                        color: 'error',
                        time: this.$store.getters.notificationDuration,
                    })
                });
        },
        onRankingDetailsClose() {
            this.rankingDetail = '';
            this.showRankingDetails = false
        },
    }
}
</script>
